import { WorkflowType } from '@communities/content-management/domain';
import {
  LabelSelector,
  type SeverityLabelOption,
  type ScoreLabelOption,
  type Label,
  type SeverityConfig,
  type ScoreConfig,
  getSeverityLabel,
  getScoreLabel,
  getScoreDescription,
} from '@core/components/LabelSelector';
import { Badge, BadgeVariant, Card } from '@soteria/design-system-ve';
import { ContentType } from '@system-config/label-management/domain';
import { useCustomLabels, useStandardLabels } from '@system-config/label-management/hooks';
import { useMemo } from 'react';

import { POLICY_COPY, PRESENCE_CONFIG } from '../../constants';
import type { DialogPolicyInfo } from '../../types';
import { useDialogContext } from '../DialogProvider';

import {
  policyInfoSummaryCard,
  cardHeader,
  cardTitle,
  cardContent,
  infoField,
  fieldLabel,
  fieldValue,
  emptyState,
  policyStatusContainer,
  policyStatusText,
} from './ReviewStep.css';
import { stepFormStyles } from './Steps.css';

/**
 * Configuration for severity-based labels
 */
const SEVERITY_CONFIG: SeverityConfig = {
  mode: 'severity' as const,
  min: 1,
  defaultValue: 2,
  defaultMax: 3,
  showDescription: true,
  getValueLabel: getSeverityLabel,
};

/**
 * Configuration for score threshold-based labels
 */
const SCORE_CONFIG: ScoreConfig = {
  mode: 'score' as const,
  min: 0,
  max: 100,
  defaultValue: 70,
  showDescription: true,
  getValueLabel: getScoreLabel,
  getValueDescription: getScoreDescription,
};

/**
 * PolicyInfo component - displays policy basic information in a clean card format
 */
function PolicyInfoSummary({ policyInfo, copy }: { policyInfo: DialogPolicyInfo; copy: (typeof POLICY_COPY)[keyof typeof POLICY_COPY] }) {
  return (
    <Card aria-labelledby="policy-info-title" className={policyInfoSummaryCard} elevation="low" padding="medium">
      <div className={cardHeader}>
        <h4 className={cardTitle} id="policy-info-title">
          Policy Information
        </h4>
      </div>
      <div className={cardContent}>
        <div className={infoField}>
          <span className={fieldLabel}>Name:</span>
          <span className={fieldValue}>{policyInfo.name || <em className={emptyState}>Not specified</em>}</span>
        </div>
        <div className={infoField}>
          <span className={fieldLabel}>Description:</span>
          <span className={fieldValue}>{policyInfo.description || <em className={emptyState}>No description provided</em>}</span>
        </div>
        <div className={infoField}>
          <span className={fieldLabel}>Policy Status:</span>
          <div className={policyStatusContainer}>
            <Badge text={policyInfo.isEnabled ? 'Enabled' : 'Disabled'} variant={policyInfo.isEnabled ? BadgeVariant.Success : BadgeVariant.Error} />
            <span className={policyStatusText}>{policyInfo.isEnabled ? copy.reviewStatusEnabled : copy.reviewStatusDisabled}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

/**
 * ReviewStep component props
 */
export interface ReviewStepProps {
  communityId: string;
}

/**
 * ReviewStep component - Final step of the dialog
 * Shows a comprehensive summary of all configured policy data for final review.
 * Uses the same LabelSelector components in readOnly mode for visual consistency.
 */
export function ReviewStep({ communityId }: ReviewStepProps) {
  const { state, workflowType } = useDialogContext();
  const { formData } = state;
  const { policyInfo, textLabels, imageLabels, usernameLabels, customLabels } = formData;
  const { labels: standardLabels, loading: labelsLoading } = useStandardLabels();

  const isModeration = workflowType === WorkflowType.MODERATION;
  const copy = POLICY_COPY[workflowType];

  // Filter standard labels for text content
  const textLabelOptions: SeverityLabelOption[] = useMemo(
    () =>
      standardLabels
        .filter(label => label.contentType === ContentType.Text)
        .map(label => ({
          id: label.id,
          labelName: label.labelName,
          maxSeverity: label.maxSeverity ?? undefined,
        })),
    [standardLabels]
  );

  // Filter standard labels for image content
  const imageLabelOptions: ScoreLabelOption[] = useMemo(
    () =>
      standardLabels
        .filter(label => label.contentType === ContentType.Image)
        .map(label => ({
          id: label.id,
          labelName: label.labelName,
        })),
    [standardLabels]
  );

  // Filter standard labels for username content (severity-based, like text)
  const usernameLabelOptions: SeverityLabelOption[] = useMemo(
    () =>
      standardLabels
        .filter(label => label.contentType === ContentType.Username)
        .map(label => ({
          id: label.id,
          labelName: label.labelName,
          maxSeverity: label.maxSeverity ?? undefined,
        })),
    [standardLabels]
  );

  // Fetch custom labels for name resolution (needed when editing existing policies)
  const { labels: fetchedCustomLabels } = useCustomLabels(isModeration ? communityId : '');

  // Custom label options for review - resolve names from fetched data or stored names
  const customLabelOptions: ScoreLabelOption[] = useMemo(
    () =>
      customLabels.customLabels.map(label => {
        const fetched = fetchedCustomLabels.find(cl => cl.id === label.labelId);

        return {
          id: label.labelId,
          labelName: fetched?.name || label.labelName || label.labelId,
        };
      }),
    [customLabels.customLabels, fetchedCustomLabels]
  );

  // Convert custom labels to LabelWithValue for LabelSelector compatibility
  const customLabelsAsScore: Label[] = useMemo(
    () => customLabels.customLabels.map(label => ({ labelId: label.labelId, scoreThreshold: 0 })),
    [customLabels.customLabels]
  );

  const noop = () => {
    /* Read-only in review step */
  };

  return (
    <div className={stepFormStyles}>
      <PolicyInfoSummary copy={copy} policyInfo={policyInfo} />

      {/* Text Content Labels - same for both workflows (severity-based) */}
      {textLabels.textLabels.length > 0 && (
        <LabelSelector
          readOnly
          availableLabels={textLabelOptions}
          config={SEVERITY_CONFIG}
          emptyStateMessage="No text labels selected."
          enableBulkSelection={false}
          loading={labelsLoading}
          selectedLabels={textLabels.textLabels}
          title="Text Content Labels"
          onChange={noop}
        />
      )}

      {/* Image Content Labels — presence-based for moderation, score-based for classification */}
      {imageLabels.imageLabels.length > 0 && isModeration && (
        <LabelSelector
          readOnly
          availableLabels={imageLabelOptions}
          config={PRESENCE_CONFIG}
          emptyStateMessage="No image labels selected."
          enableBulkSelection={false}
          loading={labelsLoading}
          selectedLabels={imageLabels.imageLabels}
          title="Image Content Labels"
          onChange={noop}
        />
      )}
      {imageLabels.imageLabels.length > 0 && !isModeration && (
        <LabelSelector
          readOnly
          availableLabels={imageLabelOptions}
          config={SCORE_CONFIG}
          emptyStateMessage="No image labels selected."
          enableBulkSelection={false}
          loading={labelsLoading}
          selectedLabels={imageLabels.imageLabels}
          title="Image Content Labels"
          onChange={noop}
        />
      )}

      {/* Username Content Labels — severity-based */}
      {usernameLabels.usernameLabels.length > 0 && (
        <LabelSelector
          readOnly
          availableLabels={usernameLabelOptions}
          config={SEVERITY_CONFIG}
          emptyStateMessage="No username labels selected."
          enableBulkSelection={false}
          loading={labelsLoading}
          selectedLabels={usernameLabels.usernameLabels}
          title="Username Content Labels"
          onChange={noop}
        />
      )}

      {/* Custom Labels (Moderation only) */}
      {isModeration && customLabelsAsScore.length > 0 && (
        <LabelSelector
          readOnly
          availableLabels={customLabelOptions}
          config={PRESENCE_CONFIG}
          emptyStateMessage="No custom labels selected."
          enableBulkSelection={false}
          loading={false}
          selectedLabels={customLabelsAsScore}
          title="Custom Labels"
          onChange={noop}
        />
      )}
    </div>
  );
}
